'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { Typography } from '@curology/ui-components-web/react/Typography';
import assert from 'assert';
import React, { useEffect, useState } from 'react';

import CartPriceSummary from '@/components/checkout/components/cartPriceSummary';
import ConsultationStayTuned from '@/components/checkout/components/consultationStayTuned';
import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useCheckout } from '@/components/flow/steps/checkout/hooks/useCheckout';
import { AdditionalDisclaimer } from '@/components/productSelectionAndCheckoutAdditionalDisclaimer';
import { DATA_KEYS } from '@/consts/dataKeys';
import { CheckoutService } from '@/services/checkoutService/client';
import {
  type CheckoutPaymentDetails,
  type Patient,
  type ShippingAddressFields,
} from '@/types/cartCheckout/types';

import CcEntrySummary from './ccEntrySummary';
import { CcForm } from './ccForm';
import ShippingAddressForm from './shippingAddressForm';
import ShippingAddressPlaceholder from './shippingAddressPlaceholder';
import { CHECKOUT_STATES } from '../consts/checkoutStates';
import { trackFieldPrePopulatedEvents } from '../js/trackFieldPrePopulatedEvents';

const CartCheckoutPage = () => {
  const { user, items, taxAmount, total, discountAmount } = useDataProvider(
    DATA_KEYS.cartCheckout,
  );
  const { brand, treatmentCategory } = useFlowContext();
  const { onSuccess } = useCheckout();

  // Page state
  const [patient, setPatient] = useState({
    id: user.id,
    firstname: user.firstName,
    lastname: user.firstName,
    address1: user.address1,
    address2: user.address2,
    city: user.city,
    email: user.email,
    brand: user.brand,
    state: user.state,
    zipcode: user.zipCode,
  } as Patient);
  const [shippingAddressFields, setShippingAddressFields] =
    useState<ShippingAddressFields>({
      apartmentSuite: user.address2 || '',
      city: user.city || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      state: user.state || '',
      streetAddress: user.address1 || '',
      zipCode: user.zipCode || '',
    });
  const [submittingForm, setSubmittingForm] = useState(false);
  const [trackedFieldPrePopulatedEvents, setTrackedFieldPrePopulatedEvents] =
    useState(false);

  // Payment state
  const [cardLastFour, setCardLastFour] = useState<string | undefined>('');
  const [checkoutState, setCheckoutState] = useState(CHECKOUT_STATES.ccEntry);
  const [paymentNonce, setPaymentNonce] = useState<string | undefined>();

  useEffect(() => {
    if (!trackedFieldPrePopulatedEvents) {
      setTrackedFieldPrePopulatedEvents(true);

      trackFieldPrePopulatedEvents(shippingAddressFields);
    }
  }, [shippingAddressFields, trackedFieldPrePopulatedEvents]);

  const processCheckout = async () => {
    setSubmittingForm(true);

    assert(paymentNonce, 'Payment nonce must be set');

    const checkoutInvoiceData = await CheckoutService.processCartCheckout({
      brand,
      treatmentCategory,
      paymentToken: paymentNonce,
    });

    if (checkoutInvoiceData) {
      onSuccess();
    } else {
      setSubmittingForm(false);
    }
  };

  const checkoutPaymentDetails: CheckoutPaymentDetails = {
    isCollectionRequired: true,
    isPaymentRequired: true,
  };

  return (
    <>
      <div className="cur-font-heading cur-mt-12">
        <Typography size="h2">Order Summary</Typography>
      </div>
      <CartPriceSummary
        items={items}
        tax={taxAmount}
        total={total}
        discount={discountAmount}
      />
      <ConsultationStayTuned />
      <div className="cur-text-h2 cur-tracking-h2 cur-leading-h2 cur-font-bold cur-font-heading cur-mt-8">
        <Typography size="h2">Checkout</Typography>
      </div>
      {(checkoutPaymentDetails.isCollectionRequired ||
        checkoutPaymentDetails.isPaymentRequired) && (
        <>
          <CcForm
            checkoutState={checkoutState}
            patient={patient}
            setCardLastFour={setCardLastFour}
            setCheckoutState={setCheckoutState}
            setPatient={setPatient}
            setPaymentNonce={setPaymentNonce}
            userMessage={checkoutPaymentDetails.userMessage}
            validateOnly={true}
          />
          <CcEntrySummary
            cardLastFour={cardLastFour}
            checkoutState={checkoutState}
            patient={patient}
            setCheckoutState={setCheckoutState}
          />
        </>
      )}
      <ShippingAddressPlaceholder
        patient={patient}
        checkoutState={checkoutState}
        setCheckoutState={setCheckoutState}
      />
      <ShippingAddressForm
        checkoutState={checkoutState}
        patient={patient}
        setPatient={setPatient}
        setShippingAddressFields={setShippingAddressFields}
        setSubmittingForm={setSubmittingForm}
        shippingAddressFields={shippingAddressFields}
        showFlashMessageOnCheckoutSuccess={true}
        submittingForm={submittingForm}
        futureShipmentTotal={0}
        onUpdate={processCheckout}
      />
      <AdditionalDisclaimer />
      {checkoutState === CHECKOUT_STATES.ccAndShippingAddressCompleted && (
        <div className="cur-mt-4">
          <Button
            className="cur-w-full"
            data-sentry-id="Confirm payment method and activate trial"
            onClick={processCheckout}
            disabled={submittingForm}
            size="lg"
          >
            Confirm payment method
          </Button>
        </div>
      )}
    </>
  );
};

export default CartCheckoutPage;
